import React, { useEffect, useState } from "react"
import styled from "styled-components"
import { useFormContext } from "react-hook-form"
import { ErrorMessage } from "@hookform/error-message"
import { kv } from "~/utils/functions"
import { Values } from "~/model/Config"
import ErrorField from "~/components/atoms/ErrorField"

/**
 * チェックボックスのプロパティ
 */

interface Props {
  /**
   * 名前
   */
  name?: string

  /**
   * クラス
   */
  className?: string

  /**
   * セパレーター
   */
  separator?: string

  /**
   * 有効・無効
   */
  disabled?: boolean

  /**
   * チェックボックス 一覧データ
   */
  dataSource: Values[] | Values

  /**
   * チェックボックス　値
   */
  value: string[] | number[] | string | number | boolean | null

  /**
   * 確認画面モード
   */
  isConfirm?: boolean

  /**
   * バリデーション
   */
  validations?: {
    required?: boolean
  }

  /**
   * エラー時のタイトル
   */
  error?: string

  /**
   * チェンジイベント
   */
  onChange?: (value: string[] | number[] | string | number | boolean | null) => void
}

export default function AcCheckBox(props: Props) {
  const {
    dataSource,
    value,
    name,
    separator = "・",
    isConfirm = false,
    disabled = false,
    validations,
    error,
    className = "",
    onChange,
  } = props
  const {
    register,
    formState: { errors },
  } = useFormContext()
  const isArrayData = Array.isArray(dataSource)
  const convertDataSource = isArrayData ? dataSource : [dataSource]
  const [currentValue, setCurrentValue] = useState<any>()

  useEffect(() => {
    setCurrentValue(
      isArrayData ? value : typeof dataSource.value === "boolean" ? value || false : value
    )
  }, [value])

  const options = {}

  if (validations?.required) {
    Object.assign(options, {
      required:
        error === null
          ? Array.isArray(dataSource)
            ? "選択してください"
            : "チェックを入れてください"
          : `${error || name}を選択してください`,
    })
  }

  const confirmView = (): string[] => {
    const selectData = (isArrayData ? currentValue : [currentValue]).filter((item) => item !== null)
    const data = kv(convertDataSource, "value")
    return selectData.map((key) => data?.[key]?.label).join(separator)
  }

  return isConfirm ? (
    <>
      <ConfirmLabel className={"form-label"}>{confirmView()}</ConfirmLabel>
    </>
  ) : (
    <>
      <div className={className}>
        {convertDataSource.map((data: Values, index) => (
          <CheckLabel disabled={disabled} key={index}>
            <Input
              type="checkbox"
              id={`check-${name}-${index}`}
              {...register(name, {
                ...options,
                onChange: (event) => {
                  const checked = event.target.checked
                  if (isArrayData) {
                    const selectValue =
                      convertDataSource[event.target.id.replace(/^(.*?)+-/i, "")].value
                    const newData = [...currentValue]
                    const index = newData.indexOf(selectValue)
                    if (index >= 0) {
                      newData.splice(index, 1)
                    }
                    if (checked) {
                      newData.push(selectValue)
                    }
                    setCurrentValue(newData)
                    onChange?.apply(this, [newData])
                  } else {
                    if (typeof data.value === "boolean") {
                      setCurrentValue(Boolean(checked))
                      onChange?.apply(this, [Boolean(checked)])
                    } else {
                      if (checked) {
                        setCurrentValue(data.value)
                        onChange?.apply(this, [data.value])
                      } else {
                        setCurrentValue(null)
                        onChange?.apply(this, [null])
                      }
                    }
                  }
                },
              })}
              value={data.value}
              checked={isArrayData ? (value as any[]).includes(data.value) : currentValue}
              disabled={disabled}
            />
            {data.label}
            <CheckMark className="checkmark"></CheckMark>
          </CheckLabel>
        ))}
        <ErrorMessage
          errors={errors}
          name={name}
          render={({ message }) => <ErrorField>{message}</ErrorField>}
        />
      </div>
    </>
  )
}

const Input = styled.input.attrs({
  type: "checkbox",
})`
  &[type="checkbox"] {
    display: none;
  }
  &.checkbox {
    background: rebeccapurple;

    width: 20px;
    height: 20px;
    margin-top: 0;
    margin-right: 11px;
    margin-bottom: 0;
    margin-left: 0;
    border-color: #bdbfc8;
    border-radius: 0;
    vertical-align: middle;

    margin-right: 0px;
    display: none;
  }

  &:checked ~ .checkmark {
    background-color: #6f6cff;
  }

  &:checked .checkmark:after {
    display: block;
  }

  &:disabled ~ .checkmark {
    //background-color: #f6f6f6;
    border: 1px solid #e6e6e6;
  }

  &:checked[disabled] ~ .checkmark {
    background-color: #eee;
  }
`

const CheckLabel = styled.label`
  position: relative;
  padding-left: 30px;
  margin-right: 20px;
  cursor: ${(props) => (props.disabled ? "unset" : "pointer")};
`

const CheckMark = styled.span`
  position: absolute;
  top: 0;
  left: 0;
  height: 20px;
  width: 20px;
  border: 1px solid #707070;
  background-color: #ffffff;

  &::after {
    content: "";
    position: absolute;
    left: 7px;
    top: 1px;
    width: 7px;
    height: 14px;
    border: solid white;
    border-width: 0 3px 3px 0;
    transform: rotate(45deg);
  }
`

const ConfirmLabel = styled.label`
  padding-left: 0px;
  margin-bottom: 0px;
`
